'use client';

import { Button } from '@/components/ui/Button';
import { ChevronDownIcon, QuoteIcon } from '@/components/ui/icons';
import { cn } from '@/lib/ui/cn';
import type {
  BlockTeamMemberBioColor,
  BlockTeamMemberBioCtaStyle,
  BlockTeamMemberBioProps,
} from '@/types/blocks';
import { addLineBreaks } from '@/utils/addLineBreaks';
import Image from 'next/image';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

const colors: Record<BlockTeamMemberBioColor, string> = {
  slate: 'bg-sh-slate',
  teal: 'bg-sh-teal',
  cream: 'bg-sh-cream',
  tan: 'bg-sh-tan',
  paper: 'bg-sh-paper',
  white: 'bg-white',
};

const ctaVariants = {
  outline: 'outline-dark',
  light: 'light',
  dark: 'dark',
  link: 'link',
} as const satisfies Record<BlockTeamMemberBioCtaStyle, string>;

export const TeamMemberBio = ({
  image,
  imageGradient,
  name,
  jobTitle,
  quote,
  bio,
  backgroundColor,
  showClinicalBackground,
  clinicalBackgroundHeading,
  entries,
  showCta,
  seeMoreText,
  seeLessText,
  ctaStyle,
  className,
  ...rest
}: BlockTeamMemberBioProps) => {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const fromHeight = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);

  const expanded = open || !showCta;
  const hasPanel = showClinicalBackground && entries.length > 0;
  const canToggle = showCta && (hasPanel || bioOverflows);
  const hasLogos = entries.some(entry => entry.logo);
  const paragraphs = (bio ?? '')
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean);
  const hasBio = paragraphs.length > 0;
  const heading = [name, jobTitle].filter(Boolean).join(' ');
  const bioId = `${id}-bio`;
  const panelId = `${id}-panel`;

  useEffect(() => {
    const el = bioRef.current;
    if (!el || expanded) return;
    const check = () => {
      if (el.getAnimations().length) return;
      setBioOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded]);

  useLayoutEffect(() => {
    const el = bioRef.current;
    const from = fromHeight.current;
    fromHeight.current = null;
    if (!el || from === null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.animate(
      [{ maxHeight: `${from}px` }, { maxHeight: `${el.offsetHeight}px` }],
      { duration: 300, easing: 'ease-out' }
    );
  }, [open]);

  const toggle = () => {
    const top = rootRef.current?.getBoundingClientRect().top ?? 0;
    if (open && top < 0) rootRef.current?.scrollIntoView({ block: 'start' });
    fromHeight.current = bioRef.current?.offsetHeight ?? null;
    setOpen(!open);
  };

  return (
    <div
      ref={rootRef}
      data-component='team-member-bio'
      data-test='team-member-bio'
      className={cn(
        '@container mx-auto w-full max-w-200 scroll-mt-44 rounded-3xl',
        colors[backgroundColor],
        className
      )}
      {...rest}
    >
      <div className='p-4 @xl:p-8'>
        <div className='flex flex-col gap-4 @xl:flex-row @xl:items-center @xl:gap-8'>
          {image ? (
            <div className='relative aspect-square w-full shrink-0 overflow-hidden rounded-3xl @xl:w-[49.3%]'>
              <Image
                src={image.url}
                alt={image.alt}
                fill
                sizes='(min-width: 768px) 363px, 100vw'
                className='object-cover'
              />
              {imageGradient && (
                <div
                  data-test='team-member-bio-gradient'
                  aria-hidden='true'
                  className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_66.83%,rgba(0,0,0,0.25)_83.41%,rgba(0,0,0,0.5)_100%)]'
                />
              )}
              {heading && (
                <h3 className='absolute inset-x-0 bottom-0 pr-7.75 pb-4 pl-4 font-sans text-2xl leading-none font-medium tracking-[-0.02em] text-white @xl:px-7.5 @xl:pb-3 @xl:text-[32px] @xl:leading-[1.3] @xl:tracking-[-0.04em]'>
                  {heading}
                </h3>
              )}
            </div>
          ) : (
            heading && (
              <h3 className='text-sh-dark-gray font-sans text-2xl leading-[1.3] font-bold'>
                {heading}
              </h3>
            )
          )}
          {quote && (
            <blockquote className='flex flex-1 flex-col items-center justify-center font-serif font-light text-black @xl:self-stretch @xl:py-4'>
              <div className='flex w-full flex-col @xl:max-w-81.5'>
                <QuoteIcon className='h-5.25 w-6' />
                <p className='px-8 text-2xl leading-none tracking-[-0.02em] @xl:text-4xl @xl:tracking-normal'>
                  {addLineBreaks(quote)}
                </p>
                <QuoteIcon className='h-5.25 w-6 rotate-180 self-end' />
              </div>
            </blockquote>
          )}
        </div>

        {hasBio && (
          <div
            id={bioId}
            ref={bioRef}
            className={cn(
              'mt-4 space-y-[1.5em] overflow-hidden font-sans text-lg leading-normal text-black @xl:mt-8 @xl:text-[21px]',
              !expanded && 'max-h-[6em] @xl:max-h-[4.5em]',
              !expanded &&
                bioOverflows &&
                '[mask-image:linear-gradient(to_bottom,black_40%,transparent)]'
            )}
          >
            {paragraphs.map((paragraph, index) => (
              <p key={index}>{addLineBreaks(paragraph)}</p>
            ))}
          </div>
        )}

        {hasPanel && (
          <div
            className={cn(
              'grid transition-all duration-300 ease-out motion-reduce:transition-none',
              expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div id={panelId} inert={!expanded} className='overflow-hidden'>
              <div className='mt-8 rounded-2xl bg-white p-4 @xl:px-8'>
                {clinicalBackgroundHeading && (
                  <h4 className='pb-2 text-center font-serif text-[27px] leading-7.75 font-light text-black @xl:text-4xl @xl:leading-[1.3]'>
                    {clinicalBackgroundHeading}
                  </h4>
                )}
                <ul className='divide-sh-gray mt-2 space-y-2 divide-y'>
                  {entries.map(entry => (
                    <li
                      key={entry.id}
                      className='flex min-h-24.25 items-center gap-3.5 @xl:min-h-30 @xl:gap-8'
                    >
                      {hasLogos && (
                        <div
                          data-test='team-member-bio-logo'
                          className='relative size-20 shrink-0 @xl:size-30'
                        >
                          {entry.logo && (
                            <Image
                              src={entry.logo.url}
                              alt={entry.logo.alt}
                              fill
                              sizes='(min-width: 768px) 120px, 80px'
                              className='object-contain'
                            />
                          )}
                        </div>
                      )}
                      <div className='font-sans text-lg leading-[1.3] text-black @xl:text-[21px]'>
                        {entry.institution && (
                          <p className='font-bold'>{entry.institution}</p>
                        )}
                        {entry.role && <p>{entry.role}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {canToggle && (
          <div className='mt-8 flex justify-center'>
            <Button
              type='button'
              variant={ctaVariants[ctaStyle]}
              aria-expanded={open}
              aria-controls={[hasBio && bioId, hasPanel && panelId]
                .filter(Boolean)
                .join(' ')}
              onClick={toggle}
              className='h-12 px-5 py-3 text-lg leading-[1.3] font-bold md:w-full md:text-lg @xl:h-15 @xl:w-auto @xl:px-7.5 @xl:text-[21px]'
            >
              {open ? seeLessText : seeMoreText}
              {ctaStyle === 'link' && (
                <ChevronDownIcon
                  className={cn(
                    'h-3 w-4.5 translate-y-1/8 transition-transform duration-300 ease-out motion-reduce:transition-none @xl:h-3.5 @xl:w-5.25',
                    open && 'translate-y-0 rotate-180'
                  )}
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
